import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { gql, useMutation } from '@apollo/client';

interface VinNoChangeDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (formData: { vehicleId: string; newVin: string }) => void;
    data: {
        vin: string;
        imei: string;
        iccid: string;
        vehicleId: string;
    };
}

const EDIT_VIN_MUTATION = gql`
  mutation Mutation($vehicleId: String!, $chassisNo: String!) {
    editVin(vehicleId: $vehicleId, chassisNo: $chassisNo) {
      status
      message
    }
  }
`;

const VinNoChangeDialog: React.FC<VinNoChangeDialogProps> = ({ open, onClose, onSubmit, data }) => {
    const [newVin, setNewVin] = useState('');
    const [editVin, { loading }] = useMutation(EDIT_VIN_MUTATION);
    const [alert, setAlert] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error';
    } | null>(null);

    const handleSubmit = async () => {
        try {
            const res = await editVin({
                variables: {
                    vehicleId: data.vehicleId,
                    chassisNo: newVin,
                },
            });

            const { status, message } = res.data.editVin;

            if (status === true) {
                setAlert({
                    open: true,
                    message: message || 'VIN updated successfully!',
                    severity: 'success'
                });

                // Update parent component with new values
                onSubmit({
                    vehicleId: data.vehicleId,
                    newVin
                });

                // Close dialog after 2 seconds
                setTimeout(() => {
                    setNewVin('');
                    onClose();
                }, 2000);
            } else {
                setAlert({
                    open: true,
                    message: message || 'Failed to update VIN',
                    severity: 'error'
                });
            }
        } catch (error: any) {
            setAlert({
                open: true,
                message: error.message || 'Error updating VIN',
                severity: 'error'
            });
        }
    };

    const handleNewVinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const sanitizedValue = value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 20);
        setNewVin(sanitizedValue);
    };

    const handleCloseAlert = () => {
        setAlert(null);
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Vin Number Change</Typography>
                    <IconButton  onClick={onClose} disabled={loading} sx={{ color: '#000' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: 2,
                        pt: 2
                    }}>
                        <TextField
                            label="Current Vin*"
                            name="vin"
                            value={data.vin}
                            disabled
                            fullWidth
                        />
                        <TextField
                            label="New Vin*"
                            name="newVin"
                            value={newVin}
                            onChange={handleNewVinChange}
                            fullWidth
                            inputProps={{ maxLength: 20 }}
                        />
                        <TextField
                            label="Imei*"
                            name="imei"
                            value={data.imei}
                            disabled
                            fullWidth
                        />
                        {/* <TextField
                            label="Iccid*"
                            name="iccid"
                            value={data.iccid}
                            disabled
                            fullWidth
                        /> */}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button
                        onClick={onClose}
                        variant='outlined'
                        disabled={loading}
                    >
                        CANCEL
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color='error'
                        disabled={loading || !newVin}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!alert}
                autoHideDuration={6000}
                onClose={handleCloseAlert}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={handleCloseAlert}
                    severity={alert?.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {alert?.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default VinNoChangeDialog;